// Advanced display of a swiper (Swiper Studio -> Settings) -- pure model.
//
// Mirror of server_elakay/elakay_helpers/swiperDisplayRules.js (same
// swiperDisplaySchema.json, byte for byte; the backend always re-validates)
// plus what only the browser needs: linked spacing / corner editing, presets
// and the CSS the renderer applies.
//
// settings.display:
//   null                    never configured -> legacy render, untouched
//   { enabled: false, ... } configured, switched off -> legacy render (kept)
//   { enabled: true, desktop, tablet, mobile }
//     desktop: every group; tablet / mobile: ONLY their own groups
//     (a missing group inherits Desktop, like the canvas element layouts).
//
// DOM of an advanced swiper (DynamicSwiper):
//   section.dsw.dsw-adv   external spacing (margin) + width mode
//     div.dsw-box         visual box: padding, border, radius, background,
//                         shadow, clipping, width / max width / alignment
//       div.dsw-frame     the slides (aspect ratio or height)
import SCHEMA from "./swiperDisplaySchema.json";

export { SCHEMA as DISPLAY_SCHEMA };
export const DISPLAY_DEVICES = SCHEMA.devices;
export const DISPLAY_GROUPS = SCHEMA.groups;

const isPlainObject = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);
const clone = (value) => JSON.parse(JSON.stringify(value));
const HEX_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const roundToStep = (value, step) => {
  if (!step || step >= 1) return Math.round(value);
  const decimals = String(step).split(".")[1]?.length || 0;
  return Number((Math.round(value / step) * step).toFixed(decimals));
};

export const clampTo = (value, bounds, fallback) => {
  if (value === null || value === undefined || value === "" || typeof value === "boolean") return fallback;
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  const [min, max, step] = bounds;
  return roundToStep(Math.min(max, Math.max(min, number)), step);
};

const pickEnum = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback);
const pickBool = (value, fallback) => (typeof value === "boolean" ? value : fallback);
const pickColor = (value, fallback) => (HEX_COLOR_PATTERN.test(String(value || "")) ? String(value) : fallback);

// Bounds of one numeric field ([min, max, step]).
export const boundsFor = (kind, unit) => (Array.isArray(SCHEMA.bounds[kind]) ? SCHEMA.bounds[kind] : SCHEMA.bounds[kind]?.[unit]);
export const unitsFor = (kind) => SCHEMA.units[kind] || ["px"];

// ─── Sanitizers (mirror of the backend) ─────────────────────────────────

const sanitizeSpacing = (kind) => (input, fallback) => {
  const source = isPlainObject(input) ? input : {};
  const unit = pickEnum(source.unit, SCHEMA.units[kind], fallback.unit);
  const bounds = SCHEMA.bounds[kind][unit];
  const out = { unit, link: pickEnum(source.link, SCHEMA.linkModes, fallback.link) };
  SCHEMA.sides.forEach((side) => {
    out[side] = clampTo(source[side], bounds, clampTo(fallback[side], bounds, 0));
  });
  return out;
};

const sanitizeBorder = (input, fallback) => {
  const source = isPlainObject(input) ? input : {};
  return {
    style: pickEnum(source.style, SCHEMA.borderStyles, fallback.style),
    width: clampTo(source.width, SCHEMA.bounds.borderWidth, fallback.width),
    color: pickColor(source.color, fallback.color),
    opacity: clampTo(source.opacity, SCHEMA.bounds.opacity, fallback.opacity),
  };
};

const sanitizeRadius = (input, fallback) => {
  const source = isPlainObject(input) ? input : {};
  const unit = pickEnum(source.unit, SCHEMA.units.radius, fallback.unit);
  const bounds = SCHEMA.bounds.radius[unit];
  const out = { unit, link: pickEnum(source.link, SCHEMA.radiusLinkModes, fallback.link) };
  SCHEMA.corners.forEach((corner) => {
    out[corner] = clampTo(source[corner], bounds, clampTo(fallback[corner], bounds, 0));
  });
  return out;
};

// Corners of the slides / images (Swiper Studio -> Settings "all slides /
// all images", Slides "this slide / this image"). null = no own value
// (inherit, or square when nothing is set anywhere); an object = these
// corners, 0 being perfectly square. Not per device.
export const ZERO_RADIUS = Object.freeze({ topLeft: 0, topRight: 0, bottomRight: 0, bottomLeft: 0, unit: "px", link: "all" });
export const sanitizeOptionalRadius = (input) => (isPlainObject(input) ? sanitizeRadius(input, ZERO_RADIUS) : null);

const sanitizeBackground = (input, fallback) => {
  const source = isPlainObject(input) ? input : {};
  return {
    mode: pickEnum(source.mode, SCHEMA.backgroundModes, fallback.mode),
    color: pickColor(source.color, fallback.color),
    opacity: clampTo(source.opacity, SCHEMA.bounds.opacity, fallback.opacity),
  };
};

const sanitizeMeasure = (kind, input, fallback) => {
  const source = isPlainObject(input) ? input : {};
  const unit = pickEnum(source.unit, SCHEMA.units[kind], fallback.unit);
  const bounds = SCHEMA.bounds[kind][unit];
  return { value: clampTo(source.value, bounds, clampTo(fallback.value, bounds, bounds[0])), unit };
};

const sanitizeSize = (input, fallback) => {
  const source = isPlainObject(input) ? input : {};
  const maxWidthSource = isPlainObject(source.maxWidth) ? source.maxWidth : {};
  let maxHeight;
  if (source.maxHeight === undefined) maxHeight = fallback.maxHeight;
  else if (source.maxHeight === null || source.maxHeight === "") maxHeight = null;
  else maxHeight = clampTo(source.maxHeight, SCHEMA.bounds.maxHeight, fallback.maxHeight);
  return {
    widthMode: pickEnum(source.widthMode, SCHEMA.widthModes, fallback.widthMode),
    width: sanitizeMeasure("width", source.width, fallback.width),
    maxWidth: {
      mode: pickEnum(maxWidthSource.mode, SCHEMA.maxWidthModes, fallback.maxWidth.mode),
      ...sanitizeMeasure("maxWidth", maxWidthSource, fallback.maxWidth),
    },
    align: pickEnum(source.align, SCHEMA.aligns, fallback.align),
    heightMode: pickEnum(source.heightMode, SCHEMA.heightModes, fallback.heightMode),
    height: sanitizeMeasure("height", source.height, fallback.height),
    maxHeight,
  };
};

// Tablet / Mobile "size": only the fields that device changed (each one
// inherits Desktop on its own, e.g. a phone-only max height keeps following
// the Desktop width mode). Every other group is overridden as a whole (its
// numbers depend on its unit).
const SIZE_FIELDS = Object.freeze(["widthMode", "width", "maxWidth", "align", "heightMode", "height", "maxHeight"]);
const sanitizeSizeOverride = (input, desktopSize) => {
  const full = sanitizeSize(input, desktopSize);
  return Object.fromEntries(SIZE_FIELDS.filter((field) => input[field] !== undefined).map((field) => [field, full[field]]));
};

const GROUP_SANITIZERS = Object.freeze({
  visible: (input, fallback) => pickBool(input, fallback),
  margin: sanitizeSpacing("margin"),
  padding: sanitizeSpacing("padding"),
  border: sanitizeBorder,
  radius: sanitizeRadius,
  background: sanitizeBackground,
  shadow: (input, fallback) => pickEnum(input, SCHEMA.shadows, fallback),
  clipContent: (input, fallback) => pickBool(input, fallback),
  size: sanitizeSize,
});

export const sanitizeGroup = (group, input, fallback) => GROUP_SANITIZERS[group](input, fallback);

const isGroupValue = (group, input) => {
  if (group === "visible" || group === "clipContent") return typeof input === "boolean";
  if (group === "shadow") return SCHEMA.shadows.includes(input);
  return isPlainObject(input);
};

export const sanitizeBox = (input, fallbackBox) => {
  const source = isPlainObject(input) ? input : {};
  return Object.fromEntries(SCHEMA.groups.map((group) => [group, GROUP_SANITIZERS[group](source[group], fallbackBox[group])]));
};

export const sanitizeOverride = (input, desktopBox) => {
  if (!isPlainObject(input)) return {};
  const out = {};
  SCHEMA.groups.forEach((group) => {
    if (input[group] === undefined || input[group] === null || !isGroupValue(group, input[group])) return;
    if (group === "size") {
      const size = sanitizeSizeOverride(input.size, desktopBox.size);
      if (Object.keys(size).length) out.size = size;
      return;
    }
    out[group] = GROUP_SANITIZERS[group](input[group], desktopBox[group]);
  });
  return out;
};

export const seedDesktopFromLegacy = (legacy) => {
  const settings = isPlainObject(legacy) ? legacy : {};
  const defaults = SCHEMA.defaults;
  const radius = clampTo(settings.borderRadius, SCHEMA.bounds.radius.px, defaults.radius.topLeft);
  let maxHeight = defaults.size.maxHeight;
  if (settings.maxHeightDesktop === null || settings.maxHeightDesktop === "") maxHeight = null;
  else if (settings.maxHeightDesktop !== undefined) maxHeight = clampTo(settings.maxHeightDesktop, SCHEMA.bounds.maxHeight, defaults.size.maxHeight);
  return {
    ...clone(defaults),
    radius: { topLeft: radius, topRight: radius, bottomRight: radius, bottomLeft: radius, unit: "px", link: "all" },
    background: { ...defaults.background, color: pickColor(settings.backgroundColor, defaults.background.color) },
    size: { ...clone(defaults.size), maxHeight },
  };
};

// First activation: identical render to the legacy settings.
export const seedDisplay = (legacy) => {
  const settings = isPlainObject(legacy) ? legacy : {};
  const desktop = seedDesktopFromLegacy(settings);
  const mobile = {};
  const mobileMax = clampTo(settings.maxHeightMobile, SCHEMA.bounds.maxHeight, null);
  if (mobileMax !== desktop.size.maxHeight) mobile.size = { maxHeight: mobileMax };
  return { enabled: true, version: SCHEMA.version, desktop, tablet: {}, mobile };
};

export const sanitizeDisplay = (input, legacySettings) => {
  if (!isPlainObject(input)) return null;
  const desktop = sanitizeBox(input.desktop, seedDesktopFromLegacy(legacySettings));
  return {
    enabled: pickBool(input.enabled, true),
    version: SCHEMA.version,
    desktop,
    tablet: sanitizeOverride(input.tablet, desktop),
    mobile: sanitizeOverride(input.mobile, desktop),
  };
};

export const isDisplayActive = (display) => isPlainObject(display) && display.enabled !== false && isPlainObject(display.desktop);

export const resolveDisplayBox = (display, device = "desktop") => {
  if (!isDisplayActive(display)) return null;
  if (device === "desktop" || !SCHEMA.devices.includes(device)) return display.desktop;
  const override = isPlainObject(display[device]) ? display[device] : {};
  return {
    ...display.desktop,
    ...override,
    size: { ...display.desktop.size, ...(isPlainObject(override.size) ? override.size : {}) },
  };
};

// ─── Editing helpers (Studio) ───────────────────────────────────────────

// Sides that move together for a link mode: "vertical" = top + bottom,
// "horizontal" = left + right, "all" = every side, "none" = only that side.
export const linkedSides = (link, side) => {
  if (link === "all") return [...SCHEMA.sides];
  if (link === "vertical" && (side === "top" || side === "bottom")) return ["top", "bottom"];
  if (link === "horizontal" && (side === "left" || side === "right")) return ["left", "right"];
  return [side];
};

export const setSpacingSide = (group, side, value) => {
  const next = { ...group };
  linkedSides(group.link, side).forEach((entry) => { next[entry] = value; });
  return next;
};

// Turning a link on aligns the linked sides on the first one of the group
// (top for "all" / "vertical", left for "horizontal"); one undo step.
export const setSpacingLink = (group, link) => {
  const next = { ...group, link };
  if (link === "all") SCHEMA.sides.forEach((side) => { next[side] = group.top; });
  if (link === "vertical") next.bottom = group.top;
  if (link === "horizontal") next.right = group.left;
  return next;
};

export const setSpacingUnit = (kind, group, unit) => {
  const bounds = boundsFor(kind, unit);
  const next = { ...group, unit };
  SCHEMA.sides.forEach((side) => { next[side] = clampTo(group[side], bounds, 0); });
  return next;
};

export const setRadiusCorner = (radius, corner, value) => (radius.link === "all"
  ? { ...radius, topLeft: value, topRight: value, bottomRight: value, bottomLeft: value }
  : { ...radius, [corner]: value });

export const setRadiusLink = (radius, link) => (link === "all"
  ? { ...radius, link, topRight: radius.topLeft, bottomRight: radius.topLeft, bottomLeft: radius.topLeft }
  : { ...radius, link });

export const setRadiusUnit = (radius, unit) => {
  const bounds = boundsFor("radius", unit);
  const next = { ...radius, unit };
  SCHEMA.corners.forEach((corner) => { next[corner] = clampTo(radius[corner], bounds, 0); });
  return next;
};

// Groups this device overrides (Desktop overrides nothing).
export const overriddenGroups = (display, device) => (device === "desktop" || !isPlainObject(display?.[device])
  ? []
  : SCHEMA.groups.filter((group) => display[device][group] !== undefined));

// Writes one group for one device: Desktop changes the base every device
// inherits; Tablet / Mobile only ever write their own override.
export const withDeviceGroup = (display, device, group, value) => {
  if (device === "desktop") return { ...display, desktop: { ...display.desktop, [group]: value } };
  const override = { ...(display[device] || {}) };
  if (group === "size") {
    // Only the size fields that differ from Desktop (each inherits on its own).
    const own = Object.fromEntries(Object.entries(value).filter(([field, fieldValue]) => (
      JSON.stringify(fieldValue) !== JSON.stringify(display.desktop.size[field])
    )));
    if (Object.keys(own).length) override.size = own;
    else delete override.size;
    return { ...display, [device]: override };
  }
  override[group] = value;
  return { ...display, [device]: override };
};

// Tablet / Mobile: forget the override (inherit Desktop again). Desktop:
// back to the schema default of that group.
export const resetDeviceGroup = (display, device, group) => {
  if (device === "desktop") return { ...display, desktop: { ...display.desktop, [group]: clone(SCHEMA.defaults[group]) } };
  const { [group]: _removed, ...rest } = display[device] || {};
  return { ...display, [device]: rest };
};

export const PRESET_IDS = Object.freeze(Object.keys(SCHEMA.presets));

// A preset only touches the visual box (spacing, border, corners, shadow,
// width mode) of ONE device and is a single undo step: slides, background,
// visibility and every other device stay as they are.
export const applyPreset = (box, presetId) => {
  const preset = SCHEMA.presets[presetId];
  if (!preset) return box;
  const next = { ...box };
  Object.entries(preset).forEach(([group, value]) => {
    if (isPlainObject(value) && isPlainObject(box[group])) {
      next[group] = group === "size"
        ? { ...box.size, ...clone(value) }
        : { ...box[group], ...clone(value) };
    } else {
      next[group] = clone(value);
    }
  });
  return sanitizeBox(next, box);
};

// Preset on one device: Desktop changes its base box; Tablet / Mobile get
// the preset's groups as their own values (Desktop untouched).
export const applyPresetToDevice = (display, device, presetId) => {
  const preset = SCHEMA.presets[presetId];
  const effective = resolveDisplayBox(display, device);
  if (!preset || !effective) return display;
  const next = applyPreset(effective, presetId);
  if (device === "desktop") return { ...display, desktop: next };
  return Object.keys(preset).reduce((current, group) => withDeviceGroup(current, device, group, next[group]), display);
};

// Which preset (if any) the box matches, for the "active" state of a preset button.
export const matchingPreset = (box) => PRESET_IDS.find((presetId) => JSON.stringify(applyPreset(box, presetId)) === JSON.stringify(box)) || null;

// ─── CSS (DynamicSwiper renders exactly this) ───────────────────────────

const SHADOW_CSS = Object.freeze({
  none: "none",
  soft: "0 4px 14px rgba(15, 23, 42, 0.08)", // the legacy frame shadow
  medium: "0 10px 30px rgba(15, 23, 42, 0.16)",
  strong: "0 18px 48px rgba(15, 23, 42, 0.28)",
});

const hexToRgb = (hex) => {
  let value = String(hex || "").replace("#", "");
  if (value.length === 3) value = value.split("").map((char) => char + char).join("");
  const number = parseInt(value, 16);
  if (!Number.isFinite(number) || value.length !== 6) return null;
  return [(number >> 16) & 255, (number >> 8) & 255, number & 255];
};

export const colorWithOpacity = (hex, opacity) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return "transparent";
  if (opacity >= 1) return hex;
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${opacity})`;
};

const length = (value, unit) => `${Number(value) || 0}${unit}`;
// Viewport height units follow the preview's simulated screen when there is
// one (--dsw-vh-unit, set by the Studio preview), the real viewport otherwise.
const heightLength = ({ value, unit }) => (unit === "vh" ? `calc(${Number(value) || 0} * var(--dsw-vh-unit, 1vh))` : length(value, unit));

// Runtime guards (the sanitizer already bounds every value): a horizontal
// margin never takes more than a quarter of the container, a padding never
// more than a fifth, so the slides can never collapse to nothing on a small
// screen whatever the unit.
const guardedMargin = (value, unit) => (unit === "%" ? length(value, unit) : `min(${length(value, unit)}, ${SCHEMA.guards.marginInlineMaxPercent}%)`);
const guardedPadding = (value, unit) => (unit === "%" ? length(value, unit) : `min(${length(value, unit)}, ${SCHEMA.guards.paddingMaxPercent}%)`);

// Frame corner radius inside the padding + border, so a padded rounded box
// keeps concentric corners.
const innerRadius = (radius, padding, border, corner) => {
  const r = radius[corner];
  if (radius.unit === "%") return `${r}%`;
  const [vertical, horizontal] = {
    topLeft: ["top", "left"], topRight: ["top", "right"], bottomRight: ["bottom", "right"], bottomLeft: ["bottom", "left"],
  }[corner];
  const pad = `min(${guardedPadding(padding[vertical], padding.unit)}, ${guardedPadding(padding[horizontal], padding.unit)})`;
  return `max(0px, calc(${r}px - ${pad} - ${border}px))`;
};

// CSS border-radius of a corners object ("" = none).
export const radiusCss = (radius) => (radius && SCHEMA.corners.some((corner) => Number(radius[corner]) > 0)
  ? SCHEMA.corners.map((corner) => length(radius[corner], radius.unit)).join(" ")
  : "");

export const borderWidthOf = (box) => (box.border.style === "none" ? 0 : box.border.width);

// frame = { width, height } of the device frame (first slide / device
// shape). Returns the inline styles + class names of the three layers. The
// full width breakout (needs the page geometry) is measured by the renderer.
export const displayLayout = (box, frame) => {
  const { margin, padding, radius, size } = box;
  const frameWidth = Number(frame?.width) || 16;
  const frameHeight = Number(frame?.height) || 9;
  const ratio = frameWidth / frameHeight;
  const border = borderWidthOf(box);
  const widthMode = size.widthMode;
  const fills = widthMode === "fill" || widthMode === "full";

  const outerStyle = {
    marginTop: length(margin.top, margin.unit),
    marginBottom: length(margin.bottom, margin.unit),
  };
  if (widthMode !== "full") {
    const left = guardedMargin(margin.left, margin.unit);
    const right = guardedMargin(margin.right, margin.unit);
    // Fill parent also supports an embedding context that deliberately sets
    // --dsw-slot-pad. Public Elakay slots default this variable to zero, so
    // only the author's own margin/padding settings create spacing.
    outerStyle.marginLeft = widthMode === "fill" ? `calc(${left} - var(--dsw-slot-pad, 0px))` : left;
    outerStyle.marginRight = widthMode === "fill" ? `calc(${right} - var(--dsw-slot-pad, 0px))` : right;
  }

  const paddingCss = {
    paddingTop: guardedPadding(padding.top, padding.unit),
    paddingRight: guardedPadding(padding.right, padding.unit),
    paddingBottom: guardedPadding(padding.bottom, padding.unit),
    paddingLeft: guardedPadding(padding.left, padding.unit),
  };

  // Width caps of the box (border-box): the chosen max width, and -- legacy
  // behaviour -- a max height narrows a ratio-driven frame instead of
  // distorting it.
  const caps = [];
  if (!fills) {
    if (size.maxWidth.mode === "full") caps.push("100%");
    if (size.maxWidth.mode === "custom") caps.push(length(size.maxWidth.value, size.maxWidth.unit));
    if (size.heightMode === "auto" && size.maxHeight) {
      caps.push(`calc(${Math.round(size.maxHeight * ratio)}px + ${paddingCss.paddingLeft} + ${paddingCss.paddingRight} + ${border * 2}px)`);
    }
  }
  const boxStyle = {
    width: widthMode === "custom" ? `min(100%, ${length(size.width.value, size.width.unit)})` : "100%",
    maxWidth: caps.length ? `min(100%, ${caps.join(", ")})` : "none",
    marginLeft: size.align === "left" ? 0 : "auto",
    marginRight: size.align === "right" ? 0 : "auto",
    ...paddingCss,
    border: border > 0 ? `${border}px ${box.border.style} ${colorWithOpacity(box.border.color, box.border.opacity)}` : "none",
    borderRadius: SCHEMA.corners.map((corner) => length(radius[corner], radius.unit)).join(" "),
    background: box.background.mode === "transparent" ? "transparent" : colorWithOpacity(box.background.color, box.background.opacity),
    boxShadow: SHADOW_CSS[box.shadow] || "none",
    overflow: box.clipContent ? "hidden" : "visible",
  };

  const frameStyle = {
    borderRadius: box.clipContent
      ? SCHEMA.corners.map((corner) => innerRadius(radius, padding, border, corner)).join(" ")
      : 0,
  };
  if (size.heightMode === "custom") frameStyle.height = heightLength(size.height);
  else if (size.heightMode === "auto") frameStyle.aspectRatio = `${frameWidth} / ${frameHeight}`;
  // A max height that cannot narrow the frame (fill / full width, fixed
  // height) caps its height instead: images crop with their own fit.
  if (size.maxHeight && (fills || size.heightMode !== "auto")) frameStyle.maxHeight = `${size.maxHeight}px`;

  const outerClass = [
    "dsw-adv",
    `dsw-w-${widthMode}`,
    `dsw-hm-${size.heightMode}`,
    `dsw-align-${size.align}`,
  ].join(" ");
  return { outerClass, outerStyle, boxStyle, frameStyle };
};
